import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, Mail, ArrowLeft, CheckCircle, Lock, ShieldCheck, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { otpAPI, passwordResetAPI } from '../services/api';
import { validateEmail } from '../utils/validation';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1: Send OTP
  const handleSendOtp = async () => {
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await otpAPI.send(email, 'password_reset');
      if (response.success) {
        setStep(2);
        startResendTimer();
        toast({
          title: 'OTP Sent',
          description: `Verification code sent to ${email}`
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Make sure the email is registered.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      setError('Please enter 4-digit OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await otpAPI.verify(email, otp, 'password_reset');
      if (response.success) {
        setStep(3);
        toast({
          title: 'OTP Verified',
          description: 'Now set your new password'
        });
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await passwordResetAPI.resetWithOTP(email, newPassword);
      if (response.success) {
        setStep(4);
        toast({
          title: 'Password Reset Successful',
          description: 'You can now login with your new password'
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-4 relative">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-3 shadow-lg">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1 text-sm">Password Recovery</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg dark:text-foreground">
              {step === 1 && 'Forgot Password'}
              {step === 2 && 'Verify OTP'}
              {step === 3 && 'Set New Password'}
              {step === 4 && 'Password Reset'}
            </CardTitle>
            <CardDescription className="text-sm">
              {step === 1 && 'Enter your email to receive a verification code'}
              {step === 2 && `Enter the 4-digit code sent to ${email}`}
              {step === 3 && 'Create a new secure password'}
              {step === 4 && 'Your password has been reset successfully'}
            </CardDescription>
            {/* Step indicator */}
            {step < 4 && (
              <div className="flex items-center gap-2 mt-3">
                <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Step 1: Email Entry */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSendOtp}
                  disabled={loading || !email || !validateEmail(email)}
                  className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Verification Code
                </Button>
              </div>
            )}

            {/* Step 2: OTP Entry */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="text-center py-2">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-100 dark:bg-teal-900/30 rounded-full mb-3">
                    <Mail className="w-7 h-7 text-teal-600" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">We sent a code to</p>
                  <p className="font-semibold text-gray-900 dark:text-foreground">{email}</p>
                </div>
                <div className="flex justify-center">
                  <Input
                    type="text"
                    maxLength={4}
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setOtp(val);
                      setError('');
                    }}
                    placeholder="0000"
                    className="text-center text-3xl font-bold tracking-[0.5em] w-44 h-14"
                  />
                </div>
                <p className="text-center text-xs text-gray-500 dark:text-muted-foreground">OTP valid for 30 minutes</p>
                <Button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 4}
                  className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Verify OTP
                </Button>
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setStep(1); setOtp(''); setError(''); }}
                    className="text-sm"
                  >
                    ← Change Email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResendOtp}
                    disabled={resendTimer > 0 || loading}
                    className="text-sm"
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg mb-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 font-medium">Email Verified</p>
                    <p className="text-sm text-green-800">{email}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 8 characters"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      className="pl-10 pr-10 h-11"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:text-muted-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleResetPassword}
                  disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
                  className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Reset Password
                </Button>
              </div>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-foreground mb-2">
                    Password Reset Successful!
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">
                    Your password has been changed. You can now login with your new password.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                >
                  Go to Login
                </Button>
              </div>
            )}

            {step === 1 && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-600 dark:text-muted-foreground hover:text-teal-600 transition-colors inline-flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Powered by footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-xs text-gray-500 dark:text-muted-foreground">
          Powered by <span className="font-semibold text-teal-600">MYONK</span>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
